import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Trash2, Save, ChevronDown, ChevronUp, GripVertical,
  CheckCircle, XCircle, Loader2, Award, FileQuestion, Clock,
  Users, BarChart3, Edit2, Eye, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminQuizzes() {
  const navigate = useNavigate();
  const [contents, setContents] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    passing_score: 70,
    time_limit_minutes: null,
    max_attempts: 3,
    is_required_for_certificate: true,
    questions: []
  });

  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load contents that the user can manage
      const contentsRes = await axios.get(`${API_URL}/api/educational/catalog?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContents(contentsRes.data.items || []);
      
      // For now, we'll load quizzes when a content is selected
    } catch (err) {
      setError('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const loadQuizForContent = async (contentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/quizzes/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.has_quiz) {
        setEditingQuiz(res.data);
        setFormData({
          title: res.data.quiz.title,
          description: res.data.quiz.description || '',
          passing_score: res.data.quiz.passing_score,
          time_limit_minutes: res.data.quiz.time_limit_minutes,
          max_attempts: res.data.quiz.max_attempts,
          is_required_for_certificate: res.data.quiz.is_required_for_certificate,
          questions: res.data.questions.map(q => ({
            id: q.id,
            question_text: q.question_text,
            question_type: q.question_type,
            options: q.options || [],
            correct_answer: q.correct_answer || '',
            explanation: q.explanation || '',
            points: q.points || 1
          }))
        });
      } else {
        setEditingQuiz(null);
        resetForm();
      }
    } catch (err) {
      console.error('Error loading quiz:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      passing_score: 70,
      time_limit_minutes: null,
      max_attempts: 3,
      is_required_for_certificate: true,
      questions: []
    });
  };

  const handleContentSelect = async (content) => {
    setSelectedContent(content);
    setShowForm(true);
    await loadQuizForContent(content.id);
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          id: `new-${Date.now()}`,
          question_text: '',
          question_type: 'multiple_choice',
          options: [
            { id: 'a', text: '' },
            { id: 'b', text: '' },
            { id: 'c', text: '' },
            { id: 'd', text: '' }
          ],
          correct_answer: '',
          explanation: '',
          points: 1
        }
      ]
    }));
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? {
              ...q,
              options: q.options.map((opt, j) => 
                j === optionIndex ? { ...opt, text: value } : opt
              )
            }
          : q
      )
    }));
  };

  const handleSave = async () => {
    if (!selectedContent) return;
    
    // Validation
    if (!formData.title.trim()) {
      setError('El título es requerido');
      return;
    }
    if (formData.questions.length === 0) {
      setError('Debes agregar al menos una pregunta');
      return;
    }
    
    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      if (!q.question_text.trim()) {
        setError(`La pregunta ${i + 1} no tiene texto`);
        return;
      }
      if (!q.correct_answer) {
        setError(`La pregunta ${i + 1} no tiene respuesta correcta`);
        return;
      }
      if (q.question_type === 'multiple_choice') {
        const emptyOptions = q.options.filter(o => !o.text.trim());
        if (emptyOptions.length > 0) {
          setError(`La pregunta ${i + 1} tiene opciones vacías`);
          return;
        }
      }
    }

    try {
      setSaving(true);
      setError('');
      
      const payload = {
        content_id: selectedContent.id,
        title: formData.title,
        description: formData.description,
        passing_score: formData.passing_score,
        time_limit_minutes: formData.time_limit_minutes || null,
        max_attempts: formData.max_attempts,
        is_required_for_certificate: formData.is_required_for_certificate,
        questions: formData.questions.map(q => ({
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.question_type === 'multiple_choice' ? q.options : [],
          correct_answer: q.correct_answer,
          explanation: q.explanation,
          points: q.points
        }))
      };

      await axios.post(`${API_URL}/api/quizzes/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Examen guardado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
      
      // Reload quiz data
      await loadQuizForContent(selectedContent.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error guardando el examen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Exámenes</h1>
          <p className="text-gray-600 mt-2">
            Crea y administra exámenes para tus cursos y contenidos educativos
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">×</button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Content List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h2 className="font-semibold text-gray-900 mb-4">Selecciona un contenido</h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {contents.map(content => (
                  <button
                    key={content.id}
                    onClick={() => handleContentSelect(content)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedContent?.id === content.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-medium text-gray-900 truncate">{content.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                        {content.content_type}
                      </span>
                      {content.has_certificate && (
                        <Award className="w-3 h-3 text-amber-500" />
                      )}
                    </div>
                  </button>
                ))}
                {contents.length === 0 && (
                  <p className="text-gray-500 text-center py-4">No hay contenidos disponibles</p>
                )}
              </div>
            </div>
          </div>

          {/* Quiz Form */}
          <div className="lg:col-span-2">
            {showForm && selectedContent ? (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {editingQuiz ? 'Editar Examen' : 'Crear Nuevo Examen'}
                    </h2>
                    <p className="text-gray-500 text-sm">Para: {selectedContent.title}</p>
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 flex items-center gap-2"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar
                  </button>
                </div>

                {/* Basic Info */}
                <div className="space-y-4 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Título del examen *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Ej: Examen Final - Ventas Inmobiliarias"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descripción
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      rows={2}
                      placeholder="Instrucciones o descripción del examen"
                    />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Puntaje para aprobar (%)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={formData.passing_score}
                        onChange={(e) => setFormData(prev => ({ ...prev, passing_score: parseInt(e.target.value) || 70 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tiempo límite (min)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.time_limit_minutes || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, time_limit_minutes: parseInt(e.target.value) || null }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="Sin límite"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Máx. intentos
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={formData.max_attempts}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_attempts: parseInt(e.target.value) || 3 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_required_for_certificate}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_required_for_certificate: e.target.checked }))}
                          className="w-4 h-4 text-indigo-600 rounded"
                        />
                        <span className="text-sm text-gray-700">Requerido para certificado</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Questions */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">
                      Preguntas ({formData.questions.length})
                    </h3>
                    <button
                      onClick={addQuestion}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Agregar pregunta
                    </button>
                  </div>

                  <div className="space-y-6">
                    {formData.questions.map((question, qIndex) => (
                      <div key={question.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start justify-between mb-4">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm font-medium">
                            Pregunta {qIndex + 1}
                          </span>
                          <button
                            onClick={() => removeQuestion(qIndex)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Texto de la pregunta *
                            </label>
                            <textarea
                              value={question.question_text}
                              onChange={(e) => updateQuestion(qIndex, 'question_text', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                              rows={2}
                              placeholder="Escribe la pregunta aquí..."
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tipo de pregunta
                              </label>
                              <select
                                value={question.question_type}
                                onChange={(e) => updateQuestion(qIndex, 'question_type', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                              >
                                <option value="multiple_choice">Opción múltiple</option>
                                <option value="true_false">Verdadero/Falso</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Puntos
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={question.points}
                                onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value) || 1)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                              />
                            </div>
                          </div>

                          {/* Options for multiple choice */}
                          {question.question_type === 'multiple_choice' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Opciones de respuesta
                              </label>
                              <div className="space-y-2">
                                {question.options.map((option, oIndex) => (
                                  <div key={option.id} className="flex items-center gap-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`correct-${qIndex}`}
                                        checked={question.correct_answer === option.id}
                                        onChange={() => updateQuestion(qIndex, 'correct_answer', option.id)}
                                        className="w-4 h-4 text-green-600"
                                      />
                                      <span className={`w-6 h-6 flex items-center justify-center rounded text-sm font-medium ${
                                        question.correct_answer === option.id
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-gray-200 text-gray-600'
                                      }`}>
                                        {option.id.toUpperCase()}
                                      </span>
                                    </label>
                                    <input
                                      type="text"
                                      value={option.text}
                                      onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                                      placeholder={`Opción ${option.id.toUpperCase()}`}
                                    />
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Selecciona el círculo de la respuesta correcta
                              </p>
                            </div>
                          )}

                          {/* True/False */}
                          {question.question_type === 'true_false' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Respuesta correcta
                              </label>
                              <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`tf-${qIndex}`}
                                    checked={question.correct_answer === 'true'}
                                    onChange={() => updateQuestion(qIndex, 'correct_answer', 'true')}
                                    className="w-4 h-4 text-green-600"
                                  />
                                  <span className={`px-3 py-1 rounded ${
                                    question.correct_answer === 'true'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    Verdadero
                                  </span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`tf-${qIndex}`}
                                    checked={question.correct_answer === 'false'}
                                    onChange={() => updateQuestion(qIndex, 'correct_answer', 'false')}
                                    className="w-4 h-4 text-green-600"
                                  />
                                  <span className={`px-3 py-1 rounded ${
                                    question.correct_answer === 'false'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    Falso
                                  </span>
                                </label>
                              </div>
                            </div>
                          )}

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Explicación (opcional)
                            </label>
                            <input
                              type="text"
                              value={question.explanation}
                              onChange={(e) => updateQuestion(qIndex, 'explanation', e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                              placeholder="Se muestra después de responder"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {formData.questions.length === 0 && (
                      <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                        <FileQuestion className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                        <p className="text-gray-500 mb-3">No hay preguntas aún</p>
                        <button
                          onClick={addQuestion}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                        >
                          Agregar primera pregunta
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stats if editing */}
                {editingQuiz && (
                  <div className="mt-8 pt-6 border-t">
                    <h3 className="font-semibold text-gray-900 mb-4">Estadísticas</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <Users className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                        <p className="text-2xl font-bold text-blue-700">{editingQuiz.user_status?.attempts_count || 0}</p>
                        <p className="text-sm text-blue-600">Intentos totales</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <CheckCircle className="w-6 h-6 mx-auto text-green-600 mb-2" />
                        <p className="text-2xl font-bold text-green-700">
                          {editingQuiz.user_status?.has_passed ? 'Sí' : 'No'}
                        </p>
                        <p className="text-sm text-green-600">Has aprobado</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4 text-center">
                        <BarChart3 className="w-6 h-6 mx-auto text-amber-600 mb-2" />
                        <p className="text-2xl font-bold text-amber-700">
                          {Math.round(editingQuiz.user_status?.best_score || 0)}%
                        </p>
                        <p className="text-sm text-amber-600">Mejor puntaje</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <FileQuestion className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Selecciona un contenido
                </h3>
                <p className="text-gray-500">
                  Elige un curso o contenido de la lista para crear o editar su examen
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
